import React, { useEffect, useMemo, useState } from 'react';
import dynamic from 'next/dynamic';
import { type SalesFunnelData, useFunnelData } from 'utils/funnel-data-context';
import { Elements } from '@stripe/react-stripe-js';

import { trackViewedProduct } from 'utils/klaviyo';
import { useHeadingFontOverrides, useTextFontOverrides } from 'utils/font-override';
import { buildCssVar } from 'utils/style-override';
import useLocalCurrency from 'utils/useLocalCurrency';
import { convertPrice } from 'utils/price';
import { BuyFromType, BuyFromAvailability } from 'constant';

import AfterpayWidget from 'generic/AfterpayWidget';
import FunnelCartCarousel from './components/FunnelCartCarousel';
import FunnelCartBuyFrom from './components/FunnelCartBuyFrom';
import FunnelCartProduct from './components/FunnelCartProduct';
import FunnelCartSaveThis from './components/FunnelCartSaveThis';
import FunnelCartQuantitySelector from './components/FunnelCartQuantitySelector';

import { getStripe } from '../../utils/stripe-context';

import type { ComponentsFunnelCart, ComponentsFunnelCartOffer } from 'queries/graphql';

// Load stickyBuyButton dynamically
const StickyBuyButton = dynamic(() => import('./components/StickyBuyButton'), { ssr: false });

import styles from './styles.module.scss';

export default function FunnelCart({
  sectionId,
  afterpayWidget,
  buyFromBrandLogo,
  buyFromBrandName,
  quantitySelector,
  saveThis,
  showProductUnitPrice,
  defaultPaymentOption,
  offers,
  style,
}: ComponentsFunnelCart) {
  const {
    headingColor,
    buttonColor,
    buttonTextColor,
    buttonBorderColor,
    buttonHoverColor,
    buttonHoverTextColor,
    buttonHoverBorderColor,
    buyButtonBannerBackgroundColor,
    headingFont,
    textFont,
    offerStyle,
  } = style || {};

  const { isAfterpaySupported, brandExternalName, store } = useFunnelData<SalesFunnelData>();
  const { currency, rate: localFxRate } = useLocalCurrency();
  const brandName = buyFromBrandName ?? brandExternalName;

  const [selectedOffer, setSelectedOffer] = useState<ComponentsFunnelCartOffer>(offers[0]);
  const rawOffer = selectedOffer?.salesPageOffer?.offer;

  const [selectedBuyFromType, setSelectedBuyFromType] = useState(BuyFromType.VELOCITY);

  const velocityBuyFrom = useMemo(
    () => selectedOffer?.buyFroms.find(item => item.type === BuyFromType.VELOCITY),
    [selectedOffer]
  );

  const amazonBuyFrom = useMemo(
    () => selectedOffer?.buyFroms.find(item => item.type === BuyFromType.AMAZON),
    [selectedOffer]
  );

  useEffect(() => {
    if (!amazonBuyFrom || amazonBuyFrom.availability !== BuyFromAvailability.AVAILABLE) {
      setSelectedBuyFromType(BuyFromType.VELOCITY);
    }
  }, [amazonBuyFrom]);

  useEffect(() => {
    offers.forEach(offer => trackViewedProduct(offer?.salesPageOffer?.offer));
  }, [offers]);

  const headingFontOverrides = useHeadingFontOverrides(headingFont);
  const textFontOverrides = useTextFontOverrides(textFont);

  if (!offers || offers.length === 0) {
    console.error('No offers found for FunnelCart', {
      sectionId,
    });

    return null;
  }

  return (
    <Elements stripe={getStripe(store?.chargeType, store?.connectedStripeAccountId)}>
      <div id={sectionId} className={`root ${styles.main} funnelCart`} data-cy="funnel-cart">
        <style jsx>{`
          .root {
            ${buildCssVar(
              '--buy-button-banner-background-color',
              buyButtonBannerBackgroundColor,
              'var(--default-background-color)'
            )}
            ${buildCssVar('--heading-color', headingColor, 'var(--heading-color-dark)')}
              ${buildCssVar('--button-color', buttonColor, 'var(--primary-color)')}
              ${buildCssVar('--button-text-color', buttonTextColor, 'var(--text-color-light)')}
              ${buildCssVar('--button-border-color', buttonBorderColor, 'var(--primary-color)')}
              ${buildCssVar('--button-hover-color', buttonHoverColor, 'var(--primary-color)')}
              ${buildCssVar(
              '--button-hover-text-color',
              buttonHoverTextColor,
              'var(--text-color-light)'
            )}
              ${buildCssVar(
              '--button-hover-border-color',
              buttonHoverBorderColor,
              'var(--primary-color)'
            )}
              ${headingFontOverrides ?? ''}
              ${textFontOverrides ?? ''}
          }
        `}</style>

        <h2 className={styles.products_title}>Select Product</h2>
        <FunnelCartCarousel
          items={offers.map(funnelOffer => (
            <FunnelCartProduct
              key={funnelOffer.salesPageOfferId}
              onClick={setSelectedOffer}
              isSelected={
                funnelOffer.salesPageOffer.offerId === selectedOffer.salesPageOffer.offerId
              }
              funnelOffer={funnelOffer}
              showProductUnitPrice={showProductUnitPrice}
              offerStyle={offerStyle}
            />
          ))}
        />
        {quantitySelector && <FunnelCartQuantitySelector funnelOffer={selectedOffer} />}
        <h2 className={styles.products_title}>Buy From </h2>
        <div className={styles.buy_from_btn_wrapper}>
          <FunnelCartBuyFrom
            type={BuyFromType.VELOCITY}
            logo={buyFromBrandLogo}
            brandName={brandName}
            offer={rawOffer}
            buyFrom={velocityBuyFrom}
            isSelected={selectedBuyFromType === BuyFromType.VELOCITY}
            onClick={setSelectedBuyFromType}
          />
          <FunnelCartBuyFrom
            type={BuyFromType.AMAZON}
            offer={rawOffer}
            buyFrom={amazonBuyFrom}
            isSelected={selectedBuyFromType === BuyFromType.AMAZON}
            onClick={setSelectedBuyFromType}
          />
        </div>
        {isAfterpaySupported && afterpayWidget && (
          <AfterpayWidget
            price={convertPrice(rawOffer.price, localFxRate)}
            currency={currency}
            {...afterpayWidget}
          />
        )}
        {saveThis && <FunnelCartSaveThis />}
        <StickyBuyButton
          buyFromType={selectedBuyFromType}
          offer={rawOffer}
          defaultPaymentOption={defaultPaymentOption}
        />
      </div>
    </Elements>
  );
}
